import React from 'react';
import '../styles/HelpModal.css';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="help-modal-overlay" onClick={handleClose}>
      <div className="help-modal" onClick={e => e.stopPropagation()}>
        <div className="help-modal-header">
          <h1>Welcome to Infrasec Test Suite System</h1>
          <p className="help-subtitle">Your comprehensive guide to managing test documentation</p>
          <button className="help-modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="help-modal-body">
          <div className="help-section">
            <h2>What is the Infrasec Test Suite?</h2>
            <p>
              The Infrasec Test Suite System is designed to help your company manage and track compliance testing 
              for financial regulations. As a company user, you can upload test documentation, track progress, 
              and ensure your business stays compliant with Swedish tax and accounting requirements.
            </p>
          </div>

          <div className="help-section">
            <h2>Your Dashboard Overview</h2>
            <div className="feature-list">
              <div className="feature-item">
                <div>
                  <strong>Test Suite:</strong> View and manage your assigned compliance tests
                </div>
              </div>
              <div className="feature-item">
                <div>
                  <strong>Downloads:</strong> Access completed test reports and documentation
                </div>
              </div>
              <div className="feature-item">
                <div>
                  <strong>Certificates:</strong> View your company's compliance certificates
                </div>
              </div>
              <div className="feature-item">
                <div>
                  <strong>Notifications:</strong> Stay updated on test status changes and requirements
                </div>
              </div>
            </div>
          </div>

          <div className="help-section">
            <h2>Working with Test Cases</h2>
            
            <div className="step-guide">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Understanding Test Status</h3>
                  <ul>
                    <li><span className="status-indicator todo"></span> <strong>To Do:</strong> Test awaiting documentation upload</li>
                    <li><span className="status-indicator progress"></span> <strong>In Progress:</strong> Documentation uploaded, under review</li>
                    <li><span className="status-indicator passed"></span> <strong>Passed:</strong> Test completed successfully</li>
                    <li><span className="status-indicator failed"></span> <strong>Failed:</strong> Test requires additional documentation</li>
                  </ul>
                </div>
              </div>

              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Uploading Test Documentation</h3>
                  <ul>
                    <li>Click on any test card that shows "Upload Test" or needs documentation</li>
                    <li>In the popup window, click the <strong>"Upload Receipt"</strong> button</li>
                    <li>Select your document file (PDF, images, or other formats accepted)</li>
                    <li>Add any necessary comments to explain your submission</li>
                    <li>Your document will be automatically submitted for review</li>
                  </ul>
                </div>
              </div>

              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Updating or Replacing Documents</h3>
                  <ul>
                    <li>Click on test cards that already have uploaded documents</li>
                    <li>Use <strong>"Upload New Receipt"</strong> to replace existing documentation</li>
                    <li>Add comments explaining why you're updating the document</li>
                    <li>Previous versions are kept for audit purposes</li>
                  </ul>
                </div>
              </div>

              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Adding Comments and Communication</h3>
                  <ul>
                    <li>Use the comment section in each test popup to communicate with administrators</li>
                    <li>Provide context about your uploads or ask questions</li>
                    <li>View administrator responses and guidance in the comment history</li>
                    <li>Comments help ensure clear communication throughout the testing process</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="help-section">
            <h2>Important Notes</h2>
            <div className="important-notes">
              <div className="note-item">
                <div>
                  <strong>Status Changes:</strong> As a company user, you cannot change test status directly. 
                  Status updates are handled by administrators after reviewing your submissions.
                </div>
              </div>
              <div className="note-item">
                <div>
                  <strong>Mobile Access:</strong> The system works on mobile devices. Swipe left/right 
                  to navigate between test categories on smaller screens.
                </div>
              </div>
              <div className="note-item">
                <div>
                  <strong>Real-time Updates:</strong> Check notifications regularly for status updates 
                  and new test requirements from your administrators.
                </div>
              </div>
            </div>
          </div>

          <div className="help-section">
            <h2>Understanding Your Test Categories</h2>
            <p>Your tests are organized into different VAT categories based on Swedish tax requirements:</p>
            <div className="vat-categories">
              <div className="vat-item">
                <span className="vat-rate">25%</span>
                <span>Normal sales with 25% VAT rate</span>
              </div>
              <div className="vat-item">
                <span className="vat-rate">12%</span>
                <span>Reduced VAT rate for specific goods/services</span>
              </div>
              <div className="vat-item">
                <span className="vat-rate">6%</span>
                <span>Lowest VAT rate for essential items</span>
              </div>
              <div className="vat-item">
                <span className="vat-rate">Mixed</span>
                <span>Transactions with multiple VAT rates</span>
              </div>
            </div>
          </div>

          <div className="help-section">
            <h2>Best Practices for Success</h2>
            <div className="best-practices">
              <div className="practice-item">
                <span className="practice-number">1</span>
                <div>
                  <strong>Upload Clear Documents:</strong> Ensure receipts and invoices are legible 
                  and contain all required information for VAT compliance.
                </div>
              </div>
              <div className="practice-item">
                <span className="practice-number">2</span>
                <div>
                  <strong>Provide Context:</strong> Use comments to explain unusual transactions 
                  or provide additional context for your submissions.
                </div>
              </div>
              <div className="practice-item">
                <span className="practice-number">3</span>
                <div>
                  <strong>Regular Monitoring:</strong> Check your dashboard regularly for new 
                  test requirements and administrator feedback.
                </div>
              </div>
              <div className="practice-item">
                <span className="practice-number">4</span>
                <div>
                  <strong>Timely Submissions:</strong> Upload documentation promptly to avoid 
                  delays in your compliance certification process.
                </div>
              </div>
            </div>
          </div>

          <div className="help-section">
            <h2>Troubleshooting Common Issues</h2>
            <div className="troubleshooting">
              <div className="issue-item">
                <h4>Can't Upload Files?</h4>
                <p>Ensure your file is under 10MB and in a supported format (PDF, JPG, PNG, DOC). 
                Try refreshing the page and attempting the upload again.</p>
              </div>
              <div className="issue-item">
                <h4>Test Status Not Updating?</h4>
                <p>Remember that only administrators can change test status. Your upload will be 
                reviewed, and status will update once the administrator has processed your submission.</p>
              </div>
              <div className="issue-item">
                <h4>Missing Notifications?</h4>
                <p>Click the notification bell in the top-right corner to view all updates. 
                Notifications show test status changes and new requirements.</p>
              </div>
              <div className="issue-item">
                <h4>Mobile Display Issues?</h4>
                <p>On mobile devices, use horizontal swipe gestures to navigate between test 
                categories. Rotate your device to landscape mode for better visibility.</p>
              </div>
            </div>
          </div>

          <div className="help-section contact-section">
            <h2>Need Additional Help?</h2>
            <p>
              If you have questions not covered in this guide or need personalized assistance 
              with your compliance testing, our support team is here to help.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <div>
                  <strong>Email Support:</strong> 
                  <a href="mailto:par.ahren@infrasec.se" className="contact-link">
                    par.ahren@infrasec.se
                  </a>
                </div>
              </div>
              <div className="contact-item">
                <div>
                  <strong>Response Time:</strong> We typically respond within 24 hours during business days
                </div>
              </div>
              <div className="contact-item">
                <div>
                  <strong>Languages:</strong> Support available in Swedish and English
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="help-modal-footer">
          <div className="footer-content">
            <p>This guide covers the essential features for company users. For administrator functions or advanced features, please contact your system administrator.</p>
            <button className="close-help-button" onClick={handleClose}>
              Got it, let's get started!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;